import { COLLECTIONS } from '../../constants/collections';
import { VEHICLE_STATUS, APPROVAL_STATUS } from '../../constants/statuses';
import { createDocument, getDocument, getAllDocuments, updateDocument, deleteDocument } from './firestoreService';

// Helper to construct path
const getCollectionPath = (tenantId) => `tenants/${tenantId}/${COLLECTIONS.VEHICLES}`;

// Create vehicle
export const createVehicle = async (vehicleData, tenantId) => {
    if (!tenantId) throw new Error('Tenant ID is required for creating vehicle');

    const initialStatus = vehicleData.status || VEHICLE_STATUS.PENDING_APPROVAL;

    const data = {
        ...vehicleData,
        tenantId, // Ensure tenantId is stored in doc too
        status: initialStatus,
        approvalStatus: vehicleData.approvalStatus || APPROVAL_STATUS.COORDINATOR_APPROVED,
        statusHistory: [{
            status: initialStatus,
            timestamp: new Date(),
            note: 'Araç kaydı oluşturuldu'
        }],
        driver: vehicleData.driver || {
            name: null,
            phone: null,
            idNumber: null
        },
        licensePlate: vehicleData.licensePlate || null,
        facilityEnterTime: null,
        facilityExitTime: null,
        loadingStartTime: null,
        loadingEndTime: null,
        actualLoadingTime: null,
        notes: [],
        proposedAlternativeTimes: []
    };

    return await createDocument(getCollectionPath(tenantId), data);
};

// Update vehicle (Full update)
export const updateVehicle = async (vehicleId, vehicleData, tenantId) => {
    if (!tenantId) throw new Error('Tenant ID is required for updating vehicle');
    return await updateDocument(getCollectionPath(tenantId), vehicleId, vehicleData);
};

// Get vehicle by ID
export const getVehicle = async (vehicleId, tenantId) => {
    if (!tenantId) throw new Error('Tenant ID is required for fetching vehicle');
    return await getDocument(getCollectionPath(tenantId), vehicleId);
};

// Get vehicles with filters
export const getVehicles = async (filters = {}) => {
    const { tenantId, ...otherFilters } = filters;
    if (!tenantId) throw new Error('Tenant ID is required for listing vehicles');

    const conditions = [];

    // filters.tenantId is implicitly handled by the path, but we can keep it if needed for usage
    // No need to add 'where tenantId' clause if we are already in the tenant's collection, 
    // but keeping it doesn't hurt. However, removing it is cleaner for subcollections.

    if (otherFilters.transporterId) {
        conditions.push({ type: 'where', field: 'transporterId', operator: '==', value: otherFilters.transporterId });
    }

    if (otherFilters.status) {
        conditions.push({ type: 'where', field: 'status', operator: '==', value: otherFilters.status });
    }

    if (otherFilters.dockId) {
        conditions.push({ type: 'where', field: 'reservedDockId', operator: '==', value: otherFilters.dockId });
    }

    /* 
       Ordering in memory to avoid "missing index" errors for transporterId + createdAt.
       If performance becomes an issue with large datasets, a composite index should be created.
    */
    // conditions.push({ type: 'orderBy', field: 'createdAt', direction: 'desc' });

    if (otherFilters.limitCount) {
        conditions.push({ type: 'limit', value: otherFilters.limitCount });
    }

    const docs = await getAllDocuments(getCollectionPath(tenantId), conditions);

    // Manual sort
    return docs.sort((a, b) => {
        const timeA = a.createdAt?.toDate?.() || a.createdAt || 0;
        const timeB = b.createdAt?.toDate?.() || b.createdAt || 0;
        return timeB - timeA;
    });
};

// Update vehicle status
export const updateVehicleStatus = async (vehicleId, newStatus, note = '', userId = '', userName = '', tenantId) => {
    if (!tenantId) throw new Error('Tenant ID is required');

    const vehicle = await getVehicle(vehicleId, tenantId);
    const statusHistory = vehicle.statusHistory || [];

    statusHistory.push({
        status: newStatus,
        timestamp: new Date(),
        userId,
        userName,
        note
    });

    return await updateDocument(getCollectionPath(tenantId), vehicleId, {
        status: newStatus,
        statusHistory
    });
};

// Approve reservation (Transporter)
export const approveReservation = async (vehicleId, driverInfo, tenantId) => {
    if (!tenantId) throw new Error('Tenant ID is required');

    return await updateDocument(getCollectionPath(tenantId), vehicleId, {
        driver: driverInfo.driver,
        licensePlate: driverInfo.licensePlate,
        approvalStatus: APPROVAL_STATUS.BOTH_APPROVED,
        status: VEHICLE_STATUS.APPROVED
    });
};

// Propose alternative time
export const proposeAlternativeTime = async (vehicleId, proposal, tenantId) => {
    if (!tenantId) throw new Error('Tenant ID is required');

    const vehicle = await getVehicle(vehicleId, tenantId);
    const alternatives = vehicle.proposedAlternativeTimes || [];

    alternatives.push({
        ...proposal,
        timestamp: new Date(),
        status: 'pending'
    });

    return await updateDocument(getCollectionPath(tenantId), vehicleId, {
        proposedAlternativeTimes: alternatives
    });
};

// Accept/Reject alternative
export const respondToAlternative = async (vehicleId, alternativeIndex, accept, tenantId) => {
    if (!tenantId) throw new Error('Tenant ID is required');

    const vehicle = await getVehicle(vehicleId, tenantId);
    const alternatives = vehicle.proposedAlternativeTimes || [];

    if (alternatives[alternativeIndex]) {
        alternatives[alternativeIndex].status = accept ? 'accepted' : 'rejected';

        const updates = { proposedAlternativeTimes: alternatives };

        if (accept) {
            updates.reservedTimeSlot = alternatives[alternativeIndex].timeSlot;
            updates.reservedDockId = alternatives[alternativeIndex].dockId;
        }

        return await updateDocument(getCollectionPath(tenantId), vehicleId, updates);
    }
};

// Facility check-in
export const checkInVehicle = async (vehicleId, updates = {}, tenantId) => {
    if (!tenantId) throw new Error('Tenant ID is required');
    return await updateDocument(getCollectionPath(tenantId), vehicleId, {
        ...updates,
        facilityEnterTime: new Date(),
        status: VEHICLE_STATUS.FACILITY_ENTERED
    });
};

// Start loading
export const startLoading = async (vehicleId, tenantId) => {
    if (!tenantId) throw new Error('Tenant ID is required');
    return await updateDocument(getCollectionPath(tenantId), vehicleId, {
        loadingStartTime: new Date(),
        status: VEHICLE_STATUS.LOADING_STARTED
    });
};

// Complete loading
export const completeLoading = async (vehicleId, tenantId) => {
    if (!tenantId) throw new Error('Tenant ID is required');
    const vehicle = await getVehicle(vehicleId, tenantId);
    const loadingStart = vehicle.loadingStartTime;
    const loadingEnd = new Date();

    const actualTime = loadingStart ? Math.round((loadingEnd - loadingStart) / 1000 / 60) : 0; // minutes

    return await updateDocument(getCollectionPath(tenantId), vehicleId, {
        loadingEndTime: loadingEnd,
        actualLoadingTime: actualTime,
        status: VEHICLE_STATUS.LOADING_COMPLETED
    });
};

// Facility check-out
export const checkOutVehicle = async (vehicleId, tenantId) => {
    if (!tenantId) throw new Error('Tenant ID is required');
    const vehicle = await getVehicle(vehicleId, tenantId);

    const updates = {
        facilityExitTime: new Date(),
        status: VEHICLE_STATUS.FACILITY_EXITED
    };

    // Auto-complete loading time if not done
    if (!vehicle.loadingEndTime && vehicle.loadingStartTime) {
        updates.loadingEndTime = new Date();
        updates.actualLoadingTime = Math.round((new Date() - vehicle.loadingStartTime) / 1000 / 60);
    }

    return await updateDocument(getCollectionPath(tenantId), vehicleId, updates);
};

// Add note to vehicle
export const addVehicleNote = async (vehicleId, note, tenantId) => {
    if (!tenantId) throw new Error('Tenant ID is required');
    const vehicle = await getVehicle(vehicleId, tenantId);
    const notes = vehicle.notes || [];

    notes.push({
        ...note,
        timestamp: new Date()
    });

    return await updateDocument(getCollectionPath(tenantId), vehicleId, { notes });
};

// Delete vehicle
export const deleteVehicle = async (vehicleId, tenantId) => {
    if (!tenantId) throw new Error('Tenant ID is required');
    return await deleteDocument(getCollectionPath(tenantId), vehicleId);
};
